import { Link } from "react-router-dom";
import { MapPin, Phone, Instagram, Facebook, Map as MapIcon } from "lucide-react";
import { RESTAURANT, IMAGES } from "../lib/restaurant";
import { useLang } from "../i18n/LanguageContext";

export default function Footer() {
  const year = new Date().getFullYear();
  const { t } = useLang();

  const NAV = [
    { to: "/",        label: t.nav.home },
    { to: "/o-nas",   label: t.nav.about },
    { to: "/menu",    label: t.nav.menu },
    { to: "/galeria", label: t.nav.gallery },
    { to: "/kontakt", label: t.nav.contact },
  ];

  return (
    <footer data-testid="site-footer" className="bg-charcoal text-cream/85 border-t border-white/5">
      <div className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-8 grid md:grid-cols-12 gap-6 items-start">
          <p className="md:col-span-3 uppercase-track text-xs text-gold">{t.footer.info}</p>
          <p className="md:col-span-9 text-xs sm:text-sm text-cream/80 leading-relaxed">{t.footer.disclaimer}</p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
        <div className="md:col-span-4">
          <div className="flex items-center gap-4 mb-5">
            <img src={IMAGES.logo} alt="Segreto" className="h-16 w-16 rounded-full object-cover ring-1 ring-gold/30" />
            <div>
              <h3 className="font-serif text-2xl text-cream leading-tight">Segreto</h3>
              <p className="uppercase-track text-[10px] text-cream/75">Ristorante Italiano</p>
            </div>
          </div>
          <p className="text-sm text-cream/85 leading-relaxed">
            <span className="text-cream">{t.footer.tagline}</span>
            <br />
            {t.footer.blurb}
          </p>
        </div>

        <div className="md:col-span-2">
          <h4 className="uppercase-track text-xs text-gold mb-5">{t.footer.nav}</h4>
          <ul className="space-y-3">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} data-testid={`footer-nav-${n.to.replace("/", "") || "home"}`} className="text-sm text-cream/85 hover:text-gold transition">
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="uppercase-track text-xs text-gold mb-5">{t.footer.contact}</h4>
          <ul className="space-y-4 text-sm">
            <li>
              <p className="text-cream font-medium mb-2">Segreto Ristorante Italiano</p>
              <div className="flex gap-3 text-cream/85">
                <MapPin className="h-4 w-4 mt-1 text-gold shrink-0" />
                <span>
                  {RESTAURANT.address.street}
                  <br />
                  {RESTAURANT.address.postal}
                </span>
              </div>
            </li>
            <li>
              <p className="uppercase-track text-[10px] text-cream/75 mb-2">{t.footer.phoneLabel}</p>
              <a href={`tel:${RESTAURANT.phoneRaw}`} data-testid="footer-phone" className="flex items-center gap-2 text-cream hover:text-gold transition font-serif text-lg">
                <Phone className="h-4 w-4 text-gold" />
                {RESTAURANT.phone}
              </a>
            </li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="uppercase-track text-xs text-gold mb-5">{t.footer.social}</h4>
          <ul className="space-y-3">
            <li>
              <a href={RESTAURANT.social.instagram} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram" className="group inline-flex items-center gap-3 text-sm text-cream/85 hover:text-gold transition">
                <span className="h-9 w-9 inline-flex items-center justify-center border border-cream/20 group-hover:border-gold group-hover:bg-gold/10 transition">
                  <Instagram className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-cream">{t.footer.socialFollow1}</span>
                  <span className="block text-xs text-cream/75">@segreto_katowice</span>
                </span>
              </a>
            </li>
            <li>
              <a href={RESTAURANT.social.facebook} target="_blank" rel="noopener noreferrer" data-testid="footer-facebook" className="group inline-flex items-center gap-3 text-sm text-cream/85 hover:text-gold transition">
                <span className="h-9 w-9 inline-flex items-center justify-center border border-cream/20 group-hover:border-gold group-hover:bg-gold/10 transition">
                  <Facebook className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-cream">{t.footer.socialFollow2}</span>
                  <span className="block text-xs text-cream/75">Segreto Ristorante Italiano</span>
                </span>
              </a>
            </li>
            <li>
              <a href={RESTAURANT.social.maps} target="_blank" rel="noopener noreferrer" data-testid="footer-maps" className="group inline-flex items-center gap-3 text-sm text-cream/85 hover:text-gold transition">
                <span className="h-9 w-9 inline-flex items-center justify-center border border-cream/20 group-hover:border-gold group-hover:bg-gold/10 transition">
                  <MapIcon className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-cream">{t.footer.socialFollow3}</span>
                  <span className="block text-xs text-cream/75">{t.footer.socialSubtitle3}</span>
                </span>
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 py-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-cream/75">
          <p>© {year} Segreto Ristorante Italiano. {t.footer.rights}</p>
          <p>Pierwsza Dzielnica · Katowice</p>
        </div>
      </div>
    </footer>
  );
}
